import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import CodeBlock from "@/components/common/CodeBlock";
import { Loader2, ArrowLeft } from "lucide-react";

// Tiny markdown renderer: headings, bold, lists, paragraphs, and ```lang fenced code blocks
function renderMarkdown(md) {
  const blocks = [];
  const lines = md.split("\n");
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim() || "text";
      const code = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        code.push(lines[i]); i++;
      }
      i++;
      blocks.push(<CodeBlock key={key++} lang={lang} code={code.join("\n")}/>);
    } else if (line.startsWith("### ")) {
      blocks.push(<h3 key={key++} className="text-lg font-semibold mt-6 mb-2">{line.slice(4)}</h3>);
      i++;
    } else if (line.startsWith("## ")) {
      blocks.push(<h2 key={key++} className="text-2xl font-semibold mt-8 mb-3 tracking-tight">{line.slice(3)}</h2>);
      i++;
    } else if (line.startsWith("# ")) {
      blocks.push(<h1 key={key++} className="text-4xl font-bold mt-2 mb-4 tracking-tight">{line.slice(2)}</h1>);
      i++;
    } else if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2)); i++;
      }
      blocks.push(<ul key={key++} className="list-disc ml-5 my-3 space-y-1 text-[var(--text)]">{items.map((it, k) => <li key={k} dangerouslySetInnerHTML={{ __html: inline(it) }}/>)}</ul>);
    } else if (line.trim() === "") {
      i++;
    } else {
      blocks.push(<p key={key++} className="my-3 leading-relaxed text-[var(--text)]" dangerouslySetInnerHTML={{ __html: inline(line) }}/>);
      i++;
    }
  }
  return blocks;
}

function inline(text) {
  return text
    .replace(/`([^`]+)`/g, '<code class="font-mono px-1.5 py-0.5 rounded bg-[var(--surface-2)] border border-[var(--border)] text-[var(--gold)] text-sm">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

export default function DocPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setPage(null); setErr("");
    api.get(`/api/docs/pages/${slug}`).then(({ data }) => setPage(data)).catch((e) => setErr(String(e?.response?.data?.detail || e.message)));
  }, [slug]);

  if (err) return <div className="p-10 text-[var(--error)]">{err}</div>;
  if (!page) return <div className="p-10 flex items-center gap-2 text-[var(--text-2)]"><Loader2 className="animate-spin" size={14}/> Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto p-10" data-testid={`doc-page-${slug}`}>
      <div className="text-xs uppercase tracking-widest text-[var(--gold)] mb-2">{page.section}</div>
      <div className="prose prose-invert max-w-none">
        {renderMarkdown(page.content)}
      </div>
      <div className="mt-12 pt-6 border-t border-[var(--border)] text-xs text-[var(--text-3)] font-mono">
        Updated {new Date(page.updated_at).toLocaleString()} · {page.version}
      </div>
    </div>
  );
}
